/**
 * The two directions between an instant and a `<input type="datetime-local">`,
 * on the TENANT's clock.
 *
 * Moved out of ClockInScreen.tsx, where the hand's own clock-out edit uses
 * them, when the office correction on the approval queue needed the same pair.
 *
 * Both take the tenant's zone (`useTenantTimeZone()`). They used to read and
 * write the DEVICE's clock, so an office in Edmonton correcting a Chicago
 * crew's clock-out to 17:00 stored 17:00 Mountain — an hour off, on payroll.
 */
import { zonedParts, zonedWallClockToInstant } from './calendar-date';

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * An instant → the `YYYY-MM-DDTHH:mm` a datetime-local input wants, on the
 * tenant's clock. Seconds are dropped on purpose: the input cannot show them,
 * and a save path sends the instant only when this string CHANGED, so a round
 * trip through the picker is a no-op rather than a 30-second "edit". Every
 * caller must keep that rule: send a field only when its picker string moved.
 *
 * '' for an absent value or an unknown zone — the picker shows empty rather
 * than a guessed clock.
 */
export function toDatetimeLocal(iso: string | null | undefined, timeZone: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const p = zonedParts(d, timeZone);
  if (!p) return '';
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * The picker's value → an ISO instant, reading it as the tenant's wall clock.
 * Parsed by parts, not `new Date(str)`: an offset-less string is DEVICE-local
 * per spec, and the iOS build's engine has rejected lenient date strings before
 * (CLAUDE.md). Null for a malformed value or an unknown zone, so a save never
 * sends `Invalid Date` or a guessed offset.
 */
export function fromDatetimeLocal(value: string, timeZone: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number);
  return zonedWallClockToInstant(y, mo, d, h, mi, timeZone)?.toISOString() ?? null;
}
